import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

// Instance status type
export type EnvironmentInstanceStatus =
  | 'pending'
  | 'provisioning'
  | 'deploying'
  | 'running'
  // Stopped-pool warm-up mid-handover: still a running box, owned by the
  // maintenance sweep. Only ever seen on `pool_warmup` instances.
  | 'converting'
  | 'stopping'
  | 'terminated'
  | 'failed';

// Health status type
export type EnvironmentHealthStatus = 'healthy' | 'unhealthy' | 'unknown';

// Purpose enum
export type EnvironmentInstancePurpose =
  | 'authoring'
  | 'dev_session'
  | 'pr_test'
  | 'test_run'
  | 'flow_authoring'
  | 'unknown';

/**
 * Which agent (if any) is installed on the sandbox. Drives Chat /
 * Authoring tab gating:
 *   - `none`     → both locked
 *   - `planner`  → Chat unlocks, Authoring locked
 *   - `authoring` → both unlock
 *
 * `legacy` / `opencode` are kept for historical rows; new code writes
 * one of the three semantic values above.
 */
export type EnvironmentInstanceAgentType =
  | 'legacy'
  | 'opencode'
  | 'none'
  | 'planner'
  | 'authoring';

// Health check result type
export type HealthCheckResult = {
  name: string;
  passed: boolean;
  details: string;
  timestamp: string;
};

// Instance type matching API response
export type EnvironmentInstance = {
  id: string;
  orgId: string;
  templateId: string;
  name: string;
  status: EnvironmentInstanceStatus;
  userConfigEncrypted?: string | null;
  instanceId?: string | null;
  privateIp?: string | null;
  publicUrl?: string | null;
  isPrivate: boolean;
  instanceState?: string | null;
  healthStatus: EnvironmentHealthStatus;
  lastHealthCheck?: string | null;
  healthCheckResults?: HealthCheckResult[] | null;
  agentType: EnvironmentInstanceAgentType;
  provisionJobId?: string | null;
  logGroupName?: string | null;
  createdBy: string;
  createdAt: string;
  startedAt?: string | null;
  expiresAt: string;
  terminatedAt?: string | null;
  terminationReason?: string | null;
  purpose: EnvironmentInstancePurpose;
  reason?: string | null;
  linkedUrl?: string | null;
  /**
   * For a `failed` instance whose EC2 is held for debugging
   * (sandbox.debug.preserveFailedInstance): ISO timestamp when it will be
   * reaped. Null/absent otherwise. Superadmin-only (computed server-side).
   */
  preservedForDebugUntil?: string | null;
};

// Instance with template info (from detail endpoint)
type EnvironmentInstanceWithTemplate = EnvironmentInstance & {
  template: {
    id: string;
    name: string;
    repositoryFullName: string | null;
    branch: string;
  };
};

type ListInstancesResponse = {
  ok: boolean;
  data: EnvironmentInstance[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
};

type CreateInstancePayload = {
  templateId: string;
  name: string;
  userConfig?: Record<string, string>;
  expiresInHours?: number;
  isPrivate?: boolean;
  purpose?: EnvironmentInstancePurpose;
  reason?: string;
  linkedUrl?: string;
  withAgent?: boolean;
  /** Vanity URL prefix; hostname becomes {orgSlug}-{slug}.{envDomain} (or {slug}.{domain}) */
  subdomainSlug?: string;
  /** Attached custom domain id to host the vanity URL under (requires subdomainSlug) */
  customDomainId?: string;
};

type UpdateAnnotationsPayload = {
  reason?: string | null;
  linkedUrl?: string | null;
};

// Active statuses that require polling
const ACTIVE_STATUSES: EnvironmentInstanceStatus[] = [
  'pending',
  'provisioning',
  'deploying',
  'running',
  'converting',
  'stopping',
];

/**
 * Hook to fetch environment instances list with optional filtering and polling
 */
export function useEnvironmentInstances(options?: {
  templateId?: string;
  status?: EnvironmentInstanceStatus;
  /**
   * Whether to poll while an instance is in an active status. Defaults to
   * `true` (the dashboard wants live status). Callers that only need a
   * one-shot list — e.g. a side panel filtering on `status: 'running'`, where
   * "active" is always true and the 5s poll would run for the panel's whole
   * lifetime — should pass `false` (or gate it on their own in-progress flag).
   */
  pollWhileActive?: boolean;
}) {
  const pollWhileActive = options?.pollWhileActive ?? true;
  return useQuery({
    queryKey: ['environment-instances', options?.templateId, options?.status],
    queryFn: async (): Promise<ListInstancesResponse> => {
      const params = new URLSearchParams();
      if (options?.templateId) params.set('templateId', options.templateId);
      if (options?.status) params.set('status', options.status);

      const res = await fetch(`${API_URL}/org/environments/instances?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch instances');
      return res.json();
    },
    // Poll every 5s if any instance is in an active status
    refetchInterval: (query) => {
      if (!pollWhileActive) return false;
      const hasActive = query.state.data?.data?.some((i) => ACTIVE_STATUSES.includes(i.status));
      return hasActive ? 5000 : false;
    },
  });
}

/**
 * Hook to fetch a single instance by ID with template info
 */
export function useEnvironmentInstance(instanceId: string | null) {
  return useQuery({
    queryKey: ['environment-instance', instanceId],
    queryFn: async (): Promise<{ ok: boolean; data: EnvironmentInstanceWithTemplate }> => {
      const res = await fetch(`${API_URL}/org/environments/instances/${instanceId}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch instance');
      return res.json();
    },
    enabled: !!instanceId,
    // Poll if in an active status
    refetchInterval: (query) => {
      const status = query.state.data?.data?.status;
      return status && ACTIVE_STATUSES.includes(status) ? 5000 : false;
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Host diagnostics (AWS/EC2 metrics)
// ─────────────────────────────────────────────────────────────────────────────

export type InstanceMetricDatapoint = { t: number; v: number };

export type InstanceMetricSeries = {
  key: string;
  metricName: string;
  unit: string;
  datapoints: InstanceMetricDatapoint[];
};

export type InstanceMetrics = {
  instanceId: string;
  windowMinutes: number;
  periodSeconds: number;
  series: InstanceMetricSeries[];
};

/**
 * Host diagnostics for a sandbox instance, sourced from CloudWatch AWS/EC2
 * metrics (CPU, network, EBS throughput, status checks). `data` is null when the
 * instance has no bound EC2 host (pending/terminated) or metrics are
 * temporarily unavailable. Polls every 30s while enabled.
 */
export function useInstanceMetrics(
  instanceId: string | null,
  opts?: { windowMinutes?: number; enabled?: boolean }
) {
  const windowMinutes = opts?.windowMinutes ?? 60;
  return useQuery({
    queryKey: ['environment-instance-metrics', instanceId, windowMinutes],
    queryFn: async (): Promise<{ ok: boolean; data: InstanceMetrics | null }> => {
      const res = await fetch(
        `${API_URL}/org/environments/instances/${instanceId}/metrics?windowMinutes=${windowMinutes}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw await toApiError(res, 'Failed to fetch instance metrics');
      return res.json();
    },
    enabled: !!instanceId && (opts?.enabled ?? true),
    refetchInterval: 30_000,
  });
}

/**
 * Hook to create a new instance
 */
export function useCreateInstance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateInstancePayload) => {
      const res = await fetch(`${API_URL}/org/environments/instances`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to create instance');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['environment-instances'] });
    },
  });
}

/**
 * Hook to terminate an instance
 */
export function useTerminateInstance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (instanceId: string) => {
      const res = await fetch(`${API_URL}/org/environments/instances/${instanceId}/terminate`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to terminate instance');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['environment-instances'] });
      queryClient.invalidateQueries({ queryKey: ['environment-instance'] });
    },
  });
}

/**
 * Hook to update a sandbox instance's user-editable annotations (reason + linkedUrl).
 */
export function useUpdateInstanceAnnotations(instanceId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateAnnotationsPayload) => {
      if (!instanceId) throw new Error('Instance id is required');
      const res = await fetch(
        `${API_URL}/org/environments/instances/${instanceId}/annotations`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw await toApiError(res, 'Failed to update sandbox annotations');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['environment-instances'] });
      queryClient.invalidateQueries({ queryKey: ['environment-instance', instanceId] });
    },
  });
}

/**
 * Hook to delete an instance (must be terminated first)
 */
export function useDeleteInstance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (instanceId: string) => {
      const res = await fetch(`${API_URL}/org/environments/instances/${instanceId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to delete instance');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['environment-instances'] });
    },
  });
}
